import { useState, useEffect, useMemo } from 'react';
import { trackAdminAction } from '@/lib/analytics';
import { ChevronRight, ChevronDown, Plus, MoreHorizontal, Folder, FolderOpen, Users, Settings, Building2, List, LayoutGrid, Table as TableIcon, MapPin, DollarSign, Calendar, X, Trash2, AlertTriangle, ArrowRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { DatePicker } from '@/components/ui/date-picker';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AdminDataTable, type Column } from '@/components/settings/AdminDataTable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getAdminGroups, createAdminGroup, updateAdminGroup, deleteAdminGroup, getAdminCompanys, uploadGroupLogo, deleteGroupLogo } from '@/lib/adminApi';
import { toast } from 'react-toastify';
import { useFilters } from '@/contexts/FilterContext';
import { SettingsFilterBar } from '@/components/settings/SettingsFilterBar';
import { useLabels } from '@/hooks/useLabels';
import { CallsignsField } from '@/components/settings/CallsignsField';
import { callsignProblem, formatCallsigns, parseCallsigns } from '@/lib/callsigns';
import { AuditLogLink } from '@/components/settings/AuditLogLink';
import { ImageUploader } from '@/components/upload/ImageUploader';
import { getIconComponent } from '@/lib/iconUtils';
import { IconPicker } from '@/components/settings/IconPicker';
import { BulkRateAssignmentModal } from '@/components/settings/BulkRateAssignmentModal';
import { AutoRateToggle } from '@/components/settings/AutoRateToggle';
import { EntityRateAssignmentSection } from '@/components/settings/EntityRateAssignmentSection';
import { EntityAuditTimeline } from '@/components/settings/EntityAuditTimeline';
import { useEnergyRates } from '@/hooks/useEnergyRates';
import { authenticatedFetch } from '@/lib/api';
import { bulkParentOptions, bulkFailureMessage } from '@/lib/groupParents';
import type { BulkRateAssignmentRequest, BulkRateAssignmentResponse } from '@/types/rates';

// Minimal company shape needed for the companies dropdown + form defaults
interface CompanyOption {
  id: string;
  name: string;
  industry?: string;
}

// Helper to render companies dropdown with industry grouping
const renderCompanyOptions = (companies: CompanyOption[]) => {
  const byIndustry: Record<string, CompanyOption[]> = {};
  companies.forEach(company => {
    const industry = company.industry || 'Other';
    if (!byIndustry[industry]) byIndustry[industry] = [];
    byIndustry[industry].push(company);
  });
  const sortedIndustries = Object.keys(byIndustry).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
  return sortedIndustries.map(industry => (
    <div key={industry}>
      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {industry}
      </div>
      {byIndustry[industry].map((company) => (
        <SelectItem key={company.id} value={company.id} className="pl-4">
          {company.name}
        </SelectItem>
      ))}
    </div>
  ));
};

interface Group {
  id: string;
  name: string;
  code: string;
  parent_group_id: string | null;
  company_id: string;
  company_name?: string;
  logo_url?: string | null;
  icon?: string | null;
  location?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  callsigns?: string[];
  children?: Group[];
  level?: number;
}


// Recursive Tree Node Component
function GroupTreeNode({ 
  group, 
  onEdit, 
  onDelete, 
  onAddSite,
  singularLabel = 'Team',
  selectedGroups,
  onToggleSelection,
  groupLabel,
  siteLabel,
  groupIcon,
  siteIcon
}: { 
  group: Group; 
  onEdit: (g: Group) => void; 
  onDelete: (g: Group) => void; 
  onAddSite: (g: Group) => void;
  singularLabel?: string;
  selectedGroups: Set<string>;
  onToggleSelection: (id: string) => void;
  groupLabel: string;
  siteLabel: string;
  groupIcon: string | null;
  siteIcon: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = group.children && group.children.length > 0;
  const isSelected = selectedGroups.has(group.id);
  
  // Determine if this is a group (root) or site (has parent)
  const isSite = !!group.parent_group_id;
  // Prioritize custom icon over industry default
  const iconName = group.icon || (isSite ? siteIcon : groupIcon);

  // Get the icon component
  const IconComponent = getIconComponent(iconName);

  return (
    <div className="select-none">
      <div 
        className="flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors group/row"
        style={{ paddingLeft: `${(group.level || 0) * 24 + 12}px` }}
      >
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelection(group.id)}
            className="w-4 h-4 rounded border-border text-[#8FB560] focus:ring-[#8FB560] focus:ring-offset-0 bg-card"
          />

          <button 
            onClick={() => setExpanded(!expanded)}
            className={`p-1 rounded hover:bg-muted text-muted-foreground ${!hasChildren ? 'invisible' : ''}`}
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          
          <div className="bg-muted p-2 rounded text-[#8FB560]">
            {IconComponent ? (
              <IconComponent className="w-4 h-4" />
            ) : (
              hasChildren && expanded ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />
            )}
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-foreground">{group.name}</h3>
            {group.location && (
              <p className="text-[10px] text-muted-foreground font-mono flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {group.location}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 opacity-0 group-hover/row:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onAddSite(group)}>
                <Plus className="w-4 h-4 mr-2" /> Add Site
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(group)}>
                Edit {group.parent_group_id ? siteLabel : groupLabel}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(group)} className="text-red-400 focus:text-red-400">
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {expanded && hasChildren && (
        <div className="border-l border-border ml-6">
          {group.children!.map(child => (
            <GroupTreeNode 
              key={child.id} 
              group={child} 
              onEdit={onEdit} 
              onDelete={onDelete} 
              onAddSite={onAddSite}
              singularLabel={singularLabel}
              selectedGroups={selectedGroups}
              onToggleSelection={onToggleSelection}
              groupLabel={groupLabel}
              siteLabel={siteLabel}
              groupIcon={groupIcon}
              siteIcon={siteIcon}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Miller Columns View Component
function MillerColumnsView({
  groups,
  onEdit,
  onDelete,
  onAddSite,
  selectedGroups,
  onToggleSelection,
  groupLabel,
  siteLabel,
  groupIcon,
  siteIcon
}: {
  groups: Group[];
  onEdit: (g: Group) => void;
  onDelete: (g: Group) => void;
  onAddSite: (g: Group) => void;
  selectedGroups: Set<string>;
  onToggleSelection: (id: string) => void;
  groupLabel: string;
  siteLabel: string;
  groupIcon: string | null;
  siteIcon: string | null;
}) {
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  
  // Flatten groups to make it easier to find by ID
  const flatGroups = (groupTree: Group[]): Group[] => {
    const result: Group[] = [];
    const traverse = (items: Group[]) => {
      items.forEach(item => {
        result.push(item);
        if (item.children) {
          traverse(item.children);
        }
      });
    };
    traverse(groupTree);
    return result;
  };

  const allGroups = flatGroups(groups);

  // Build columns based on selected path
  const buildColumns = () => {
    const columns: Group[][] = [];
    
    // First column: root groups
    columns.push(groups);
    
    // Subsequent columns based on selected path
    selectedPath.forEach(groupId => {
      const group = allGroups.find(g => g.id === groupId);
      if (group?.children && group.children.length > 0) {
        columns.push(group.children);
      }
    });
    
    return columns;
  };

  const columns = buildColumns();

  const handleGroupClick = (group: Group, columnIndex: number) => {
    // Update selected path
    const newPath = selectedPath.slice(0, columnIndex);
    newPath.push(group.id);
    setSelectedPath(newPath);
  };

  return (
    <div className="select-none flex gap-4 overflow-x-auto h-full pb-2">
      {columns.map((column, columnIndex) => (
        <div
          key={columnIndex}
          className="flex-shrink-0 w-80 bg-card border border-border rounded-xl overflow-hidden flex flex-col h-full"
        >
          <div className="p-3 bg-background border-b border-border flex-shrink-0">
            <h3 className="text-sm font-medium text-foreground">
              {columnIndex === 0 ? `${groupLabel}s` : `${siteLabel}s`}
            </h3>
          </div>
          <div className="divide-y divide-border flex-1 overflow-y-auto">
            {column.map(group => {
              const isSelected = selectedPath[columnIndex] === group.id;
              const hasChildren = group.children && group.children.length > 0;
              const isChecked = selectedGroups.has(group.id);
              
              // Determine if this is a group (root) or site (has parent or not in first column)
              const isSite = columnIndex > 0 || !!group.parent_group_id;
              // Prioritize custom icon over industry default
              const iconName = group.icon || (isSite ? siteIcon : groupIcon);

              // Get the icon component
              const IconComponent = getIconComponent(iconName);

              return (
                <div
                  key={group.id}
                  className={`p-3 cursor-pointer transition-colors ${
                    isSelected ? 'bg-[#8FB560]/10 border-l-2 border-l-[#8FB560]' : 'hover:bg-muted/50'
                  }`}
                  onClick={() => handleGroupClick(group, columnIndex)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          e.stopPropagation();
                          onToggleSelection(group.id);
                        }}
                        className="w-4 h-4 rounded border-border text-[#8FB560] focus:ring-[#8FB560] focus:ring-offset-0 bg-card"
                      />
                      
                      <div className="bg-muted p-2 rounded text-[#8FB560]">
                        {IconComponent ? (
                          <IconComponent className="w-4 h-4" />
                        ) : (
                          <Folder className="w-4 h-4" />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground truncate">{group.name}</h4>
                        {group.location && (
                          <p className="text-[10px] text-muted-foreground font-mono truncate flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {group.location}
                          </p>
                        )}
                      </div>
                      
                      {hasChildren && (
                        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground flex-shrink-0">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onAddSite(group); }}>
                          <Plus className="w-4 h-4 mr-2" /> Add Site
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(group); }}>
                          Edit {group.parent_group_id ? siteLabel : groupLabel}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(group); }} className="text-red-400 focus:text-red-400">
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export function SettingsGroups() {
  const { getLabel, getIcon } = useLabels();

  // Settings-specific filters (independent from Map page)
  const {
    selectedIndustryTypes,
    selectedCompanyIds,
    selectedGroupIds,
    isLoading: filtersLoading,
    refreshFleetData
  } = useFilters();
  const [groups, setGroups] = useState<Group[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [parentGroup, setParentGroup] = useState<Group | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState<'table' | 'hierarchy'>('table');
  const [searchTerm, setSearchTerm] = useState('');

  // Bulk rate assignment state
  const [bulkRateModalOpen, setBulkRateModalOpen] = useState(false);
  const [isSubmittingRate, setIsSubmittingRate] = useState(false);
  const { rates, isLoading: isLoadingRates } = useEnergyRates({ autoFetch: true });
  
  // Get dynamic labels and icons based on user role and company industry
  const groupLabel = getLabel('group');
  const siteLabel = getLabel('site');
  const groupIcon = getIcon('group');
  const siteIcon = getIcon('site');
  
  // Determine labels based on company industry
  const getLabels = () => {
    // Parent/root items = Groups, Child items = Sites
    return {
      title: groupLabel + 's', // Pluralize
      singular: groupLabel,
      rootButton: `New ${groupLabel}`,
      emptyMessage: `No ${groupLabel.toLowerCase()}s found. Create a ${groupLabel.toLowerCase()} to get started.`,
      drawerTitle: (editing: boolean, parent: Group | null, editingItem: Group | null = null) => {
        if (editing) {
          // Check if editing item has a parent (is a Site) or is root (is a Group)
          return editingItem?.parent_group_id ? `Edit ${siteLabel}` : `Edit ${groupLabel}`;
        }
        // Creating new: parent means adding Site, no parent means creating root Group
        return parent ? `Add ${siteLabel} to ${parent.name}` : `Create ${groupLabel}`;
      },
      description: `${groupLabel}s organize units and users hierarchically`
    };
  };

  const labels = getLabels();

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    company_id: '',
    parent_group_id: '',
    logo_url: null as string | null,
    icon: null as string | null,
    location: '',
    start_date: null as string | null,
    end_date: null as string | null,
    callsigns: '',
  });

  const [bulkFormData, setBulkFormData] = useState({
    name_prefix: '',
    name_suffix: '',
    code_prefix: '',
    code_suffix: '',
    company_id: '__no_change__',
    location: '',
    parent_group_id: '__no_change__',
    start_date: null as string | null,
    end_date: null as string | null,
  });

  // Bulk delete confirmation state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load data when settings filters are initialized and when they change
  // Use JSON.stringify for stable dependency on array contents
  const selectedIndustryTypesKey = JSON.stringify(selectedIndustryTypes);
  const selectedCompanyIdsKey = JSON.stringify(selectedCompanyIds);
  const selectedGroupIdsKey = JSON.stringify(selectedGroupIds);
  useEffect(() => {
    // Don't load until filters are initialized to prevent double-loading
    if (filtersLoading) return;
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndustryTypesKey, selectedCompanyIdsKey, selectedGroupIdsKey, filtersLoading]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [groupsData, companiesData] = await Promise.all([
        getAdminGroups({ industries: selectedIndustryTypes }),
        getAdminCompanys({ industries: selectedIndustryTypes }),
      ]);
      
      // Build tree structure
      const flatGroups = groupsData.groups;
      // API groups (src/types Group, code optional) fill the local Group model
      // used by the tree; the runtime shape is unchanged, this only re-states
      // the assumption the previous `any[]` parameter left implicit.
      const tree = buildGroupTree(flatGroups as Group[]);
      
      setGroups(tree);
      setCompanies(companiesData.companies);
    } catch (error) {
      console.error('Failed to load groups:', error);
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const buildGroupTree = (items: Group[], parentId: string | null = null, level = 0): Group[] => {
    return items
      .filter(item => item.parent_group_id === parentId)
      .map(item => ({
        ...item,
        level,
        children: buildGroupTree(items, item.id, level + 1)
      }));
  };

  const handleCreateRoot = () => {
    setEditingGroup(null);
    setParentGroup(null);
    setFormData({
      name: '',
      code: '',
      company_id: companies[0]?.id || '',
      parent_group_id: '',
      logo_url: null,
      icon: null,
      location: '',
      start_date: null,
      end_date: null,
      callsigns: '',
    });
    setDrawerOpen(true);
  };

  const handleAddSite = (parent: Group) => {
    setEditingGroup(null);
    setParentGroup(parent);
    setFormData({
      name: '',
      code: '',
      company_id: parent.company_id,
      parent_group_id: parent.id,
      logo_url: null,
      icon: null,
      location: '',
      start_date: null,
      end_date: null,
      callsigns: '',
    });
    setDrawerOpen(true);
  };

  const handleEdit = (group: Group) => {
    setEditingGroup(group);
    setParentGroup(null);
    setFormData({
      name: group.name,
      code: group.code,
      company_id: group.company_id,
      parent_group_id: group.parent_group_id || '',
      logo_url: group.logo_url || null,
      icon: group.icon || null,
      location: group.location || '',
      start_date: group.start_date || null,
      end_date: group.end_date || null,
      callsigns: formatCallsigns(group.callsigns),
    });
    setDrawerOpen(true);
  };

  /**
   * Reload after a write that changed group structure.
   *
   * This page reads the uncached /admin API, so loadData() alone leaves the Map,
   * the filter sidebar and Reports serving their cached pre-mutation group list —
   * a production created here would not be selectable there until a TTL lapsed.
   */
  const reloadAfterGroupMutation = async () => {
    await loadData();
    await refreshFleetData();
  };

  const handleDelete = async (group: Group) => {
    const itemType = group.parent_group_id ? 'site' : 'group';
    const subItemType = group.parent_group_id ? 'sites' : 'sites';
    if (!confirm(`Delete ${itemType} "${group.name}"? This will delete all sub-${subItemType} and unassign units.`)) return;
    
    try {
      await deleteAdminGroup(group.id);
      trackAdminAction('group', 'deleted', group.id);
      toast.success(`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} deleted`);
      reloadAfterGroupMutation();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || `Failed to delete ${itemType}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate date relationship (if both dates are set, start must be <= end)
    if (formData.start_date && formData.end_date) {
      const startD = new Date(formData.start_date);
      const endD = new Date(formData.end_date);
      if (startD > endD) {
        toast.error('Start date must be before or equal to end date');
        return;
      }
    }

    const callsigns = parseCallsigns(formData.callsigns);
    const callsignError = callsignProblem(callsigns);
    if (callsignError) {
      toast.error(callsignError);
      return;
    }

    try {
      const payload = {
        ...formData,
        callsigns,
        parent_group_id: formData.parent_group_id || null
      };

      if (editingGroup) {
        await updateAdminGroup(editingGroup.id, payload);
        trackAdminAction('group', 'updated', editingGroup.id);
        const itemType = editingGroup.parent_group_id ? 'Site' : 'Group';
        toast.success(`${itemType} updated`);
      } else {
        await createAdminGroup(payload);
        trackAdminAction('group', 'created');
        const itemType = parentGroup ? 'Site' : 'Group';
        toast.success(`${itemType} created`);
      }
      setDrawerOpen(false);
      reloadAfterGroupMutation();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Operation failed');
    }
  };

  const handleBulkEdit = () => {
    if (selectedGroups.size === 0) {
      toast.error('Please select groups to edit');
      return;
    }
    setBulkFormData({
      name_prefix: '',
      name_suffix: '',
      code_prefix: '',
      code_suffix: '',
      company_id: '__no_change__',
      location: '',
      parent_group_id: '__no_change__',
      start_date: null,
      end_date: null,
    });
    setBulkDrawerOpen(true);
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate date relationship if both are set
    if (bulkFormData.start_date && bulkFormData.end_date) {
      const startD = new Date(bulkFormData.start_date);
      const endD = new Date(bulkFormData.end_date);
      if (startD > endD) {
        toast.error('Start date must be before or equal to end date');
        return;
      }
    }

    try {
      const flatGroups = flattenGroups(groups);
      const groupsToUpdate = flatGroups.filter(g => selectedGroups.has(g.id));

      const results = await Promise.allSettled(
        groupsToUpdate.map(group => {
          const updates: {
            name?: string;
            code?: string;
            company_id?: string;
            location?: string;
            parent_group_id?: string | null;
            start_date?: string;
            end_date?: string;
          } = {};

          // Apply name modifications if specified
          if (bulkFormData.name_prefix || bulkFormData.name_suffix) {
            updates.name = `${bulkFormData.name_prefix}${group.name}${bulkFormData.name_suffix}`;
          }

          // Apply code modifications if specified
          if (bulkFormData.code_prefix || bulkFormData.code_suffix) {
            updates.code = `${bulkFormData.code_prefix}${group.code}${bulkFormData.code_suffix}`;
          }

          // Apply company change if specified
          if (bulkFormData.company_id && bulkFormData.company_id !== '__no_change__') {
            updates.company_id = bulkFormData.company_id;
          }

          // Apply location if specified
          if (bulkFormData.location.trim()) {
            updates.location = bulkFormData.location.trim();
          }

          // Apply parent group change if specified (moving sites)
          if (bulkFormData.parent_group_id && bulkFormData.parent_group_id !== '__no_change__') {
            // __remove__ means make it a root group
            updates.parent_group_id = bulkFormData.parent_group_id === '__remove__' ? null : bulkFormData.parent_group_id;
          }

          // Apply start date if specified
          if (bulkFormData.start_date) {
            updates.start_date = bulkFormData.start_date;
          }

          // Apply end date if specified
          if (bulkFormData.end_date) {
            updates.end_date = bulkFormData.end_date;
          }

          // Only make API call if there are updates
          if (Object.keys(updates).length > 0) {
            return updateAdminGroup(group.id, updates);
          }
          return Promise.resolve();
        })
      );

      // Each group is its own request: some may land while the server refuses
      // others (ISS-123), so reload either way and say what didn't go through.
      const failure = bulkFailureMessage(results);
      if (failure) {
        toast.error(failure);
        reloadAfterGroupMutation();
        return;
      }
      toast.success(`Updated ${selectedGroups.size} ${selectedGroups.size === 1 ? labels.singular.toLowerCase() : labels.title.toLowerCase()}`);
      setBulkDrawerOpen(false);
      setSelectedGroups(new Set());
      reloadAfterGroupMutation();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to update groups');
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      const groupsToDelete = Array.from(selectedGroups);

      // Delete in sequence to handle parent-child relationships
      for (const groupId of groupsToDelete) {
        await deleteAdminGroup(groupId);
      }

      toast.success(`Deleted ${groupsToDelete.length} ${groupsToDelete.length === 1 ? labels.singular.toLowerCase() : labels.title.toLowerCase()}`);
      setBulkDeleteDialogOpen(false);
      setSelectedGroups(new Set());
      reloadAfterGroupMutation();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to delete groups');
    } finally {
      setIsDeleting(false);
    }
  };

  // Bulk rate assignment handler
  const handleBulkRateAssignment = async (data: BulkRateAssignmentRequest): Promise<BulkRateAssignmentResponse | null> => {
    setIsSubmittingRate(true);
    try {
      const response = await authenticatedFetch('/api/v1/admin/rate-assignments/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result: BulkRateAssignmentResponse = await response.json();

      if (result.created > 0) {
        toast.success(`Rate assigned to ${result.created} ${labels.singular.toLowerCase()}${result.created !== 1 ? 's' : ''}`);
      }
      if (result.skipped > 0) {
        toast.warning(`${result.skipped} ${labels.singular.toLowerCase()}${result.skipped !== 1 ? 's' : ''} skipped (overlapping dates)`);
      }
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} error${result.errors.length !== 1 ? 's' : ''} occurred`);
      }

      return result;
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to assign rates');
      return null;
    } finally {
      setIsSubmittingRate(false);
    }
  };

  const flattenGroups = (groupTree: Group[]): Group[] => {
    const result: Group[] = [];
    const traverse = (items: Group[]) => {
      items.forEach(item => {
        result.push(item);
        if (item.children) {
          traverse(item.children);
        }
      });
    };
    traverse(groupTree);
    return result;
  };

  // Filter groups based on settings filters and search term
  const filteredGroups = useMemo(() => {
    // If no filters selected, show no results
    if (selectedIndustryTypes.length === 0) return [];
    if (selectedCompanyIds.length === 0) return [];
    if (selectedGroupIds.length === 0) return [];

    // Create Sets for O(1) lookup
    const companyIdSet = new Set(selectedCompanyIds);
    const groupIdSet = new Set(selectedGroupIds.filter(id => !id.startsWith('__unassigned')));

    // Helper to check if a group or any descendant matches the group filter
    const groupOrDescendantMatches = (item: Group): boolean => {
      if (groupIdSet.size === 0) return false; // No valid group IDs = none pass
      if (groupIdSet.has(item.id)) return true;
      if (item.children) {
        return item.children.some(child => groupOrDescendantMatches(child));
      }
      return false;
    };

    // Filter tree while preserving hierarchy
    const filterTree = (items: Group[]): Group[] => {
      return items.reduce<Group[]>((acc, item) => {
        // Company filter: must match selected companies
        if (!companyIdSet.has(item.company_id)) return acc;

        // Group filter: item or descendant must match
        const matchesGroup = groupOrDescendantMatches(item);
        if (!matchesGroup) return acc;

        // Search filter
        const term = searchTerm.trim().toLowerCase();
        const matchesSearch = !term ||
          item.name?.toLowerCase().includes(term) ||
          item.code?.toLowerCase().includes(term) ||
          item.company_name?.toLowerCase().includes(term);

        const filteredChildren = item.children ? filterTree(item.children) : [];

        if (matchesSearch || filteredChildren.length > 0) {
          acc.push({
            ...item,
            children: filteredChildren
          });
        }

        return acc;
      }, []);
    };

    return filterTree(groups);
  }, [groups, searchTerm, selectedIndustryTypes, selectedCompanyIds, selectedGroupIds]);

  const toggleGroupSelection = (groupId: string) => {
    const newSelection = new Set(selectedGroups);
    if (newSelection.has(groupId)) {
      newSelection.delete(groupId);
    } else {
      newSelection.add(groupId);
    }
    setSelectedGroups(newSelection);
  };

  const selectAll = () => {
    const allIds = flattenGroups(filteredGroups).map(g => g.id);
    setSelectedGroups(new Set(allIds));
  };

  const deselectAll = () => {
    setSelectedGroups(new Set());
  };

  const groupColumns: Column<Group>[] = [
    { key: 'name', label: 'Name', sortable: true, render: (group) => (
      <div className="font-medium text-foreground flex items-center gap-2">
        <Folder className="w-4 h-4 text-[#8FB560]" />
        {group.name}
      </div>
    )},
    { key: 'code', label: 'Code', sortable: true, render: (group) => (
      <span className="font-mono text-xs bg-card px-2 py-1 rounded border border-border text-muted-foreground">
        {group.code}
      </span>
    )},
    { key: 'location', label: 'Location', sortable: true, render: (group) => (
      group.location ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="w-3 h-3" />
          {group.location}
        </div>
      ) : <span className="text-muted-foreground/50">—</span>
    )},
    { key: 'sites_count', label: `${siteLabel}s`, sortable: true, render: (group) => (
      <div className="flex items-center gap-1 text-muted-foreground">
        <Building2 className="w-3 h-3" />
        <span>{flattenGroups([group]).length - 1}</span>
      </div>
    )}
  ];

  const siteColumns: Column<Group>[] = [
    { key: 'name', label: 'Name', sortable: true, render: (site) => (
      <div className="font-medium text-foreground flex items-center gap-2">
        <Building2 className="w-4 h-4 text-[#8FB560]" />
        {site.name}
      </div>
    )},
    { key: 'code', label: 'Code', sortable: true, render: (site) => (
      <span className="font-mono text-xs bg-card px-2 py-1 rounded border border-border text-muted-foreground">
        {site.code}
      </span>
    )},
    { key: 'location', label: 'Location', sortable: true, render: (site) => (
      site.location ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="w-3 h-3" />
          {site.location}
        </div>
      ) : <span className="text-muted-foreground/50">—</span>
    )},
    { key: 'parent_name', label: groupLabel, sortable: true, render: (site) => {
      const parent = flattenGroups(groups).find(g => g.id === site.parent_group_id);
      return parent ? (
        <div className="flex items-center gap-2 text-foreground">
          <Folder className="w-3 h-3 text-muted-foreground" />
          {parent.name}
        </div>
      ) : <span className="text-muted-foreground/50">-</span>;
    }}
  ];

  return (
    <div className="space-y-6 sm:space-y-8 flex-1 flex flex-col min-h-0">
      <div className="flex flex-col gap-3 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground whitespace-nowrap">{labels.title}</h1>
              <AuditLogLink resourceTypes={['group']} tooltip="View groups audit history" />
            </div>
            <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">Manage organizational structure and hierarchy</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <SettingsFilterBar
              searchQuery={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder={`Search ${labels.title.toLowerCase()}...`}
            />
            <div className="flex gap-2">
              {selectedGroups.size > 0 && (
                <>
                  <Button onClick={handleBulkEdit} variant="outline" className="border-border hover:bg-muted whitespace-nowrap">
                    <Settings className="w-4 h-4 mr-2" />
                    Edit {selectedGroups.size}
                  </Button>
                  <Button
                    onClick={() => setBulkRateModalOpen(true)}
                    variant="outline"
                    className="border-border hover:bg-muted whitespace-nowrap"
                    disabled={isLoadingRates || rates.length === 0}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Assign Rate
                  </Button>
                  <Button
                    onClick={() => setBulkDeleteDialogOpen(true)}
                    variant="outline"
                    className="border-red-500/50 text-red-500 hover:bg-red-500/10 hover:text-red-400 whitespace-nowrap"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {selectedGroups.size}
                  </Button>
                </>
              )}
              <Button onClick={handleCreateRoot} className="bg-[#8FB560] hover:bg-[#7a9d51] whitespace-nowrap">
                <Plus className="w-4 h-4 mr-2" />
                {labels.rootButton}
              </Button>
            </div>
          </div>
        </div>

      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between flex-shrink-0">
          <TabsList className="bg-card border border-border p-1">
            <TabsTrigger value="all" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
              {labels.title}
            </TabsTrigger>
            <TabsTrigger value="sites" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
              Sites
            </TabsTrigger>
          </TabsList>
          
          {/* View Mode Toggle */}
          {activeTab === 'all' && (
            <div className="flex bg-card border border-border rounded-lg p-1">
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className={viewMode === 'table' ? 'bg-[#8FB560] hover:bg-[#7a9d51]' : 'hover:bg-muted'}
              >
                <TableIcon className="w-4 h-4 mr-2" />
                Table
              </Button>
              <Button
                variant={viewMode === 'hierarchy' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('hierarchy')}
                className={viewMode === 'hierarchy' ? 'bg-[#8FB560] hover:bg-[#7a9d51]' : 'hover:bg-muted'}
              >
                <List className="w-4 h-4 mr-2" />
                Hierarchy
              </Button>
            </div>
          )}
        </div>

        <div className="mt-6 flex-1 flex flex-col min-h-0">
          <TabsContent value="all" className="flex-1 flex-col min-h-[600px] data-[state=active]:flex">
            <div className="flex-1 flex flex-col min-h-0">
              {loading ? (
                <div className="text-muted-foreground">Loading hierarchy...</div>
              ) : groups.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>{labels.emptyMessage}</p>
                </div>
              ) : viewMode === 'table' ? (
                <AdminDataTable
                  data={filteredGroups}
                  columns={groupColumns}
                  actions={[
                    { label: `Edit ${labels.singular}`, onClick: handleEdit, icon: Settings },
                    { label: 'Delete', onClick: handleDelete, variant: 'destructive', icon: Users }
                  ]}
                  searchable
                  loading={loading}
                  emptyMessage={labels.emptyMessage}
                  tableId="groups-table"
                />
              ) : (
                <div className="flex h-full gap-6">
                  {/* Tree View - Left Panel */}
                  <div className="w-[40%] flex flex-col border-r border-border pr-6 overflow-hidden">
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-border flex-shrink-0">
                      <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                        <List className="w-4 h-4 text-muted-foreground" />
                        Tree View
                      </h3>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto pr-2">
                      {flattenGroups(filteredGroups).length > 0 && (
                        <div className="flex gap-2 mb-4">
                          <Button onClick={selectAll} variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-foreground px-2 h-7">
                            Select All
                          </Button>
                          {selectedGroups.size > 0 && (
                            <Button onClick={deselectAll} variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-foreground px-2 h-7">
                              Clear ({selectedGroups.size})
                            </Button>
                          )}
                        </div>
                      )}
                      <div className="space-y-1">
                        {filteredGroups.map(group => (
                          <GroupTreeNode
                            key={group.id}
                            group={group}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onAddSite={handleAddSite}
                            singularLabel={labels.singular}
                            selectedGroups={selectedGroups}
                            onToggleSelection={toggleGroupSelection}
                            groupLabel={groupLabel}
                            siteLabel={siteLabel}
                            groupIcon={groupIcon}
                            siteIcon={siteIcon}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Miller Columns - Right Panel */}
                  <div className="w-[60%] min-w-0 flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-border flex-shrink-0">
                      <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                        <LayoutGrid className="w-4 h-4 text-muted-foreground" />
                        Column View
                      </h3>
                    </div>
                    <div className="flex-1 min-h-0">
                      <MillerColumnsView
                        groups={filteredGroups}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onAddSite={handleAddSite}
                        selectedGroups={selectedGroups}
                        onToggleSelection={toggleGroupSelection}
                        groupLabel={groupLabel}
                        siteLabel={siteLabel}
                        groupIcon={groupIcon}
                        siteIcon={siteIcon}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="sites" className="flex-1 flex-col min-h-[600px] data-[state=active]:flex">
            <div className="flex-1 flex flex-col min-h-0">
              <AdminDataTable
                data={flattenGroups(filteredGroups).filter(g => g.parent_group_id)}
                columns={siteColumns}
                actions={[
                  { label: 'Edit Site', onClick: handleEdit, icon: Settings },
                  { label: 'Delete', onClick: handleDelete, variant: 'destructive', icon: Building2 }
                ]}
                searchable
                loading={loading}
                emptyMessage="No sites found"
                tableId="groups-sites"
              />
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Single Edit Drawer */}
      <AdminFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={labels.drawerTitle(!!editingGroup, parentGroup, editingGroup)}
        description={labels.description}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">
              {parentGroup || editingGroup?.parent_group_id ? siteLabel : groupLabel} Name
            </Label>
            <Input 
              id="name" 
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder={parentGroup || editingGroup?.parent_group_id ? 'e.g. Site A' : 'e.g. Group A'}
              required
              className="bg-background border-border"
            />
          </div>

          <div>
            <Label htmlFor="code">Code / Identifier</Label>
            <Input
              id="code"
              value={formData.code}
              onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})}
              placeholder={parentGroup || editingGroup?.parent_group_id ? 'e.g. SITE-A' : 'e.g. GROUP-A'}
              required
              className="bg-background border-border font-mono"
            />
          </div>

          <CallsignsField
            id="group-callsigns"
            value={formData.callsigns}
            onChange={(callsigns) => setFormData({ ...formData, callsigns })}
            example={parentGroup || editingGroup?.parent_group_id ? 'SDM3' : 'SDM'}
          />

          <div>
            <Label htmlFor="location" className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              Location
            </Label>
            <Input
              id="location"
              value={formData.location}
              onChange={e => setFormData({...formData, location: e.target.value})}
              placeholder="e.g. Los Angeles, CA"
              className="bg-background border-border"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Physical location or filming address
            </p>
          </div>

          {/* Production Dates - only show for parent groups (not sites) */}
          {!parentGroup && !editingGroup?.parent_group_id && (
            <div className="p-3 bg-muted/50 rounded-lg border border-border space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-medium">{groupLabel} Dates</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="start_date" className="text-xs text-muted-foreground mb-1 block">Start Date</Label>
                  <DatePicker
                    date={formData.start_date ? parseISO(formData.start_date) : undefined}
                    onDateChange={(date) => setFormData({
                      ...formData,
                      start_date: date ? format(date, 'yyyy-MM-dd') : null
                    })}
                    placeholder="Select start"
                  />
                </div>
                <div>
                  <Label htmlFor="end_date" className="text-xs text-muted-foreground mb-1 block">End Date</Label>
                  <DatePicker
                    date={formData.end_date ? parseISO(formData.end_date) : undefined}
                    onDateChange={(date) => setFormData({
                      ...formData,
                      end_date: date ? format(date, 'yyyy-MM-dd') : null
                    })}
                    placeholder="Select end"
                  />
                </div>
              </div>
              {(formData.start_date || formData.end_date) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setFormData({ ...formData, start_date: null, end_date: null })}
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear dates
                </Button>
              )}
              <p className="text-xs text-muted-foreground">
                Optional. Required to use "Production Dates" filtering.
              </p>
            </div>
          )}

          {!parentGroup && !editingGroup && (
            <div>
              <Label htmlFor="company">Company</Label>
              <Select
                value={formData.company_id}
                onValueChange={val => setFormData({...formData, company_id: val})}
              >
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Select Company" />
                </SelectTrigger>
                <SelectContent>
                  {renderCompanyOptions(companies)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Logo Upload - only show when editing existing group */}
          {editingGroup && (
            <div className="pt-2">
              <Label className="mb-2 block">
                {editingGroup.parent_group_id ? siteLabel : groupLabel} Logo
              </Label>
              <ImageUploader
                currentImage={formData.logo_url}
                onUpload={async (file) => {
                  const result = await uploadGroupLogo(editingGroup.id, file);
                  const logoUrl = result.logo.svg_url || result.logo.medium_webp_url || result.logo.medium_png_url;
                  setFormData(prev => ({ ...prev, logo_url: logoUrl }));
                  toast.success('Logo uploaded successfully');
                }}
                onRemove={async () => {
                  await deleteGroupLogo(editingGroup.id);
                  setFormData(prev => ({ ...prev, logo_url: null }));
                  toast.success('Logo removed');
                }}
                accept={{
                  'image/png': ['.png'],
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'image/webp': ['.webp'],
                  'image/gif': ['.gif'],
                  'image/svg+xml': ['.svg'],
                }}
                shape="square"
                size="md"
                helpText="PNG, SVG, JPG, WebP or GIF. Max 5MB."
              />
            </div>
          )}

          {/* Icon Selection */}
          <div className="pt-2">
            <IconPicker
              value={formData.icon}
              onChange={(icon) => setFormData(prev => ({ ...prev, icon }))}
              label={`${editingGroup?.parent_group_id || parentGroup ? siteLabel : groupLabel} Icon`}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Optional. If not set, uses the default industry icon.
            </p>
          </div>

          {/* Auto-Rate by Location - only show when editing */}
          {editingGroup && (
            <div className="p-3 bg-muted/50 rounded-lg border border-border">
              <AutoRateToggle
                entityType="group"
                entityId={editingGroup.id}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Controls automatic rate assignment for units in this {editingGroup.parent_group_id ? 'site' : 'group'}. Select "Inherit" to use the company setting.
              </p>
            </div>
          )}

          {/* Rate Assignments Section - only show when editing */}
          {editingGroup && (
            <EntityRateAssignmentSection
              entityType={editingGroup.parent_group_id ? 'site' : 'group'}
              entityId={editingGroup.id}
              entityName={editingGroup.name}
            />
          )}

          {/* Activity History - only show when editing */}
          {editingGroup && (
            <EntityAuditTimeline
              entityType={editingGroup.parent_group_id ? 'site' : 'group'}
              entityId={editingGroup.id}
              entityName={editingGroup.name}
            />
          )}

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]">
              {editingGroup ? 'Save Changes' : `Create ${parentGroup ? siteLabel : groupLabel}`}
            </Button>
            <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      {/* Bulk Edit Drawer */}
      <AdminFormDrawer
        open={bulkDrawerOpen}
        onClose={() => setBulkDrawerOpen(false)}
        title={`Bulk Edit: ${selectedGroups.size} ${selectedGroups.size === 1 ? labels.singular : labels.title}`}
        description="Apply modifications to multiple groups at once"
      >
        <form onSubmit={handleBulkSubmit} className="space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-600 dark:text-blue-400">
            Editing {selectedGroups.size} {selectedGroups.size === 1 ? labels.singular.toLowerCase() : labels.title.toLowerCase()}. Only filled fields will be applied.
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Name Modifications</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bulk_name_prefix" className="text-xs text-muted-foreground">Add Prefix</Label>
                <Input
                  id="bulk_name_prefix"
                  value={bulkFormData.name_prefix}
                  onChange={e => setBulkFormData({...bulkFormData, name_prefix: e.target.value})}
                  placeholder="e.g., 2024-"
                  className="bg-background border-border"
                />
              </div>
              <div>
                <Label htmlFor="bulk_name_suffix" className="text-xs text-muted-foreground">Add Suffix</Label>
                <Input
                  id="bulk_name_suffix"
                  value={bulkFormData.name_suffix}
                  onChange={e => setBulkFormData({...bulkFormData, name_suffix: e.target.value})}
                  placeholder="e.g., -ACTIVE"
                  className="bg-background border-border"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Code Modifications</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bulk_code_prefix" className="text-xs text-muted-foreground">Add Prefix</Label>
                <Input
                  id="bulk_code_prefix"
                  value={bulkFormData.code_prefix}
                  onChange={e => setBulkFormData({...bulkFormData, code_prefix: e.target.value.toUpperCase()})}
                  placeholder="e.g., 2024-"
                  className="bg-background border-border font-mono"
                />
              </div>
              <div>
                <Label htmlFor="bulk_code_suffix" className="text-xs text-muted-foreground">Add Suffix</Label>
                <Input
                  id="bulk_code_suffix"
                  value={bulkFormData.code_suffix}
                  onChange={e => setBulkFormData({...bulkFormData, code_suffix: e.target.value.toUpperCase()})}
                  placeholder="e.g., -A"
                  className="bg-background border-border font-mono"
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="bulk_location" className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              Set Location
            </Label>
            <Input
              id="bulk_location"
              value={bulkFormData.location}
              onChange={e => setBulkFormData({...bulkFormData, location: e.target.value})}
              placeholder="e.g., Los Angeles, CA"
              className="bg-background border-border"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Set the same location for all selected items
            </p>
          </div>

          <div>
            <Label htmlFor="bulk_company">Change Company</Label>
            <Select
              value={bulkFormData.company_id}
              onValueChange={val => setBulkFormData({...bulkFormData, company_id: val})}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="No change" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__no_change__">No change</SelectItem>
                {renderCompanyOptions(companies)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="bulk_parent" className="flex items-center gap-2">
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
              Move Under Parent {groupLabel}
            </Label>
            <Select
              value={bulkFormData.parent_group_id}
              onValueChange={val => setBulkFormData({...bulkFormData, parent_group_id: val})}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="No change" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__no_change__">No change</SelectItem>
                <SelectItem value="__remove__">Make root {groupLabel.toLowerCase()} (no parent)</SelectItem>
                {/* ISS-123: only a top-level production of the selection's company */}
                {bulkParentOptions(flattenGroups(groups), selectedGroups).map(g => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Move selected items under a {groupLabel.toLowerCase()} of the same company
            </p>
          </div>

          {/* Bulk Date Updates */}
          <div className="p-3 bg-muted/50 rounded-lg border border-border space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Set Dates</Label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bulk_start_date" className="text-xs text-muted-foreground mb-1 block">Start Date</Label>
                <DatePicker
                  date={bulkFormData.start_date ? parseISO(bulkFormData.start_date) : undefined}
                  onDateChange={(date) => setBulkFormData({
                    ...bulkFormData,
                    start_date: date ? format(date, 'yyyy-MM-dd') : null
                  })}
                  placeholder="Select start"
                />
              </div>
              <div>
                <Label htmlFor="bulk_end_date" className="text-xs text-muted-foreground mb-1 block">End Date</Label>
                <DatePicker
                  date={bulkFormData.end_date ? parseISO(bulkFormData.end_date) : undefined}
                  onDateChange={(date) => setBulkFormData({
                    ...bulkFormData,
                    end_date: date ? format(date, 'yyyy-MM-dd') : null
                  })}
                  placeholder="Select end"
                />
              </div>
            </div>
            {(bulkFormData.start_date || bulkFormData.end_date) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setBulkFormData({ ...bulkFormData, start_date: null, end_date: null })}
              >
                <X className="w-3 h-3 mr-1" />
                Clear dates
              </Button>
            )}
            <p className="text-xs text-muted-foreground">
              Set production/project dates for all selected items
            </p>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-xs text-amber-600 dark:text-yellow-400">
            Changes will be applied to {selectedGroups.size} selected {selectedGroups.size === 1 ? labels.singular.toLowerCase() : labels.title.toLowerCase()}. Empty fields will not modify existing values.
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]">
              Update {selectedGroups.size} {selectedGroups.size === 1 ? labels.singular : labels.title}
            </Button>
            <Button type="button" variant="outline" onClick={() => setBulkDrawerOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-foreground">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete {selectedGroups.size} {selectedGroups.size === 1 ? labels.singular : labels.title}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This action cannot be undone. This will permanently delete the selected {selectedGroups.size === 1 ? labels.singular.toLowerCase() : labels.title.toLowerCase()}, including all sub-{siteLabel.toLowerCase()}s and unassign any associated units.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">
            <p className="font-medium mb-2">Items to be deleted:</p>
            <ul className="list-disc list-inside space-y-1 text-xs max-h-32 overflow-y-auto">
              {flattenGroups(groups)
                .filter(g => selectedGroups.has(g.id))
                .slice(0, 10)
                .map(g => (
                  <li key={g.id}>{g.name}</li>
                ))}
              {selectedGroups.size > 10 && (
                <li className="text-muted-foreground">...and {selectedGroups.size - 10} more</li>
              )}
            </ul>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? 'Deleting...' : `Delete ${selectedGroups.size} ${selectedGroups.size === 1 ? labels.singular : labels.title}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Rate Assignment Modal */}
      <BulkRateAssignmentModal
        open={bulkRateModalOpen}
        onClose={() => setBulkRateModalOpen(false)}
        onSubmit={handleBulkRateAssignment}
        entityType="group"
        selectedEntities={flattenGroups(groups)
          .filter(g => selectedGroups.has(g.id))
          .map(g => ({
            id: g.id,
            name: g.name,
            currentRate: undefined,
          }))}
        rates={rates}
        isSubmitting={isSubmittingRate}
      />
    </div>
  );
}

